import React, { useEffect, useRef, useState } from 'react';
import s from './charts.module.css';

// Hand-rolled SVG charts (dataviz skill): thin marks, 2px surface gaps between stacked fills, recessive grid,
// a hover tooltip on every mark, a legend for ≥ 2 series. Text stays in text colours, never the series colour.

/** Categorical fallback order for series with no colour of their own — validated for the dark card surface
 *  (#221C18): lightness band, chroma, CVD ≥ 8 adjacent, contrast ≥ 3:1. Entities (projects, departments) keep
 *  their own colour; this only fills the gaps. A 7th series folds into "Other". */
export const SERIES = ['#E24A3D', '#3F7FE6', '#C98500', '#12976A', '#9A66E0', '#D9782A'];
export const OTHER = '#7D7268';

/** 1 / 2 / 2.5 / 5 × 10ⁿ — gridlines on round numbers. */
function niceStep(x) {
  const e = 10 ** Math.floor(Math.log10(Math.max(1e-9, x)));
  const f = x / e;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * e;
}

function useWidth(ref) {
  const [w, setW] = useState(800);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(200, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function useTip() {
  const [tip, setTip] = useState(null);
  const box = useRef(null);
  const show = (e, content) => {
    const r = box.current?.getBoundingClientRect();
    if (!r) return;
    setTip({ x: e.clientX - r.left, y: e.clientY - r.top, content, w: r.width });
  };
  return { box, tip, show, hide: () => setTip(null) };
}

function Tip({ tip }) {
  if (!tip) return null;
  const left = Math.min(Math.max(8, tip.x + 14), tip.w - 220);
  return <div className={s.tip} style={{ left, top: Math.max(0, tip.y - 10) }}>{tip.content}</div>;
}

/**
 * Stacked columns. data: [{ key, label, total?, segments: [{ id, label, value, color }] }]. format(v) → text.
 * One y-axis, 3 gridlines, value labels only on the tallest column and on hover.
 */
export function StackedBars({ data, height = 220, format = (v) => String(v), target = null }) {
  const { box, tip, show, hide } = useTip();
  const W = useWidth(box);
  const raw = Math.max(1, target || 0, ...data.map((d) => d.segments.reduce((a, x) => a + x.value, 0)));
  const step = niceStep(raw / 2);
  const max = Math.ceil(raw / step) * step;
  const H = height; const padL = 44; const padB = 26; const padT = 10;
  const cw = (W - padL) / Math.max(1, data.length);
  const bw = Math.min(56, cw * 0.62);
  const y = (v) => padT + (H - padT - padB) * (1 - v / max);
  const grid = Array.from({ length: Math.round(max / step) + 1 }, (_, k) => k * step);
  const tallest = data.reduce((best, d, i) => { const t = d.segments.reduce((a, x) => a + x.value, 0); return t > best.t ? { i, t } : best; }, { i: -1, t: 0 });
  return (
    <div className={s.chart} ref={box} onMouseLeave={hide}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={s.svg} role="img">
        {grid.map((g) => (
          <g key={g}>
            <line x1={padL} x2={W} y1={y(g)} y2={y(g)} className={s.grid} />
            <text x={padL - 8} y={y(g) + 4} className={s.axis} textAnchor="end">{format(g)}</text>
          </g>
        ))}
        {target ? <line x1={padL} x2={W} y1={y(target)} y2={y(target)} className={s.target} /> : null}
        {data.map((d, i) => {
          const bx = padL + i * cw + (cw - bw) / 2;
          const segs = d.segments.filter((g) => g.value > 0);
          const total = segs.reduce((a, g) => a + g.value, 0);
          let acc = 0;
          return (
            <g key={d.key} onMouseMove={(e) => show(e, (
              <>
                <div className={s.tipHead}>{d.tip || d.label}<span>{format(total)}</span></div>
                {[...segs].sort((a, b) => b.value - a.value).map((g) => (
                  <div key={g.id} className={s.tipRow}><span className={s.sw} style={{ background: g.color }} />{g.label}<span>{format(g.value)}</span></div>
                ))}
              </>
            ))}>
              <rect x={padL + i * cw} y={padT} width={cw} height={H - padT - padB} fill="transparent" />
              {segs.map((g, k) => {
                const top = y(acc + g.value); const bottom = y(acc); acc += g.value;
                const h = Math.max(0, bottom - top - (k < segs.length - 1 ? 2 : 0));   // 2px surface gap between fills
                return <rect key={g.id} x={bx} y={top} width={bw} height={h} fill={g.color} rx={k === segs.length - 1 ? 3 : 0} />;
              })}
              <text x={bx + bw / 2} y={H - 8} className={s.axis} textAnchor="middle">{d.label}</text>
              {i === tallest.i && total > 0 ? <text x={bx + bw / 2} y={y(total) - 6} className={s.val} textAnchor="middle">{format(total)}</text> : null}
            </g>
          );
        })}
      </svg>
      <Tip tip={tip} />
    </div>
  );
}

/** Rows × columns of cells, one hue light → dark by value (sequential). */
export function Heatmap({ grid, rowLabels, colLabels, format, max, cell = 22 }) {
  const { box, tip, show, hide } = useTip();
  const m = max || Math.max(1, ...grid.flat());
  return (
    <div className={s.chart} ref={box} onMouseLeave={hide}>
      <div className={s.heat} style={{ gridTemplateColumns: `44px repeat(${colLabels.length}, minmax(0, 1fr))`, '--cell': `${cell}px` }}>
        <span />
        {colLabels.map((c, i) => <span key={i} className={s.axisH}>{c}</span>)}
        {grid.map((row, r) => (
          <React.Fragment key={r}>
            <span className={s.axisH} style={{ textAlign: 'right', paddingRight: 8 }}>{rowLabels[r]}</span>
            {row.map((v, c) => (
              <span key={c} className={s.cell} style={{ background: v ? `color-mix(in oklab, var(--c-accent) ${Math.round(15 + (v / m) * 85)}%, var(--c-surface-alt))` : 'var(--c-surface-alt)' }}
                onMouseMove={(e) => show(e, <div className={s.tipHead}>{rowLabels[r]} {colLabels[c] || c}<span>{format(v)}</span></div>)} />
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className={s.scale}><span>less</span><span className={s.ramp} /><span>more</span></div>
      <Tip tip={tip} />
    </div>
  );
}

export function Legend({ items }) {
  if (!items || items.length < 2) return null;
  return <div className={s.legend}>{items.map((it) => <span key={it.id}><span className={s.sw} style={{ background: it.color }} />{it.label}</span>)}</div>;
}

/** Horizontal bars (one series): label · bar · value. */
export function HBars({ rows, format, color = 'var(--c-accent)', onPick }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className={s.hbars}>
      {rows.map((r) => {
        const inner = (<>
          <span className={s.hlabel} title={r.label}>{r.label}</span>
          <span className={s.htrack}><span style={{ width: `${(r.value / max) * 100}%`, background: r.color || color }} /></span>
          <span className="num">{format(r.value)}</span>
        </>);
        // onPick(row) → the row is a button (e.g. a tag's bar opens the tag's page); a row with pick: false stays plain.
        return onPick && r.pick !== false
          ? <button key={r.id} className={`${s.hrow} ${s.hpick}`} onClick={() => onPick(r)}>{inner}</button>
          : <div key={r.id} className={s.hrow}>{inner}</div>;
      })}
    </div>
  );
}

/**
 * A KPI tile's trend: one series, 2px line over a faint area, the last point marked; hover shows that day.
 * points: [{ key, label, value }] oldest → newest. No axes (the tile's number is the headline).
 */
export function Sparkline({ points, format = (v) => String(v), color = 'var(--c-accent)', height = 44 }) {
  const { box, tip, show, hide } = useTip();
  const W = useWidth(box);
  if (!points?.length) return <div style={{ height }} />;
  const max = Math.max(1e-9, ...points.map((p) => p.value));
  const n = points.length;
  const x = (i) => (n === 1 ? W / 2 : 3 + (i * (W - 6)) / (n - 1));
  const y = (v) => 4 + (height - 8) * (1 - v / max);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(n - 1).toFixed(1)},${height} L${x(0).toFixed(1)},${height} Z`;
  const last = points[n - 1];
  const gid = `spark${Math.round(Math.random() * 1e9)}`;
  return (
    <div className={s.chart} ref={box} onMouseLeave={hide}
      onMouseMove={(e) => {
        const r = box.current.getBoundingClientRect();
        const i = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left - 3) / Math.max(1, W - 6)) * (n - 1))));
        show(e, <div className={s.tipHead}>{points[i].label}<span>{format(points[i].value)}</span></div>);
      }}>
      <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height} className={s.svg} aria-hidden="true">
        <defs><linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".22" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
        <path d={area} fill={`url(#${gid})`} />
        <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(n - 1)} cy={y(last.value)} r="4" fill={color} stroke="var(--c-surface)" strokeWidth="2" />
      </svg>
      <Tip tip={tip} />
    </div>
  );
}

/** One whole split into parts (a 100% bar): 2px surface gaps, rounded ends, each part labelled with its value. */
export function SplitBar({ parts, format = (v) => String(v) }) {
  const { box, tip, show, hide } = useTip();
  const live = parts.filter((p) => p.value > 0);
  const total = live.reduce((a, p) => a + p.value, 0);
  return (
    <div className={s.chart} ref={box} onMouseLeave={hide}>
      <div className={s.split}>
        {live.map((p) => (
          <span key={p.id} style={{ flexGrow: p.value, background: p.color }}
            onMouseMove={(e) => show(e, <div className={s.tipHead}>{p.label}<span>{format(p.value)} · {Math.round((p.value / total) * 100)}%</span></div>)} />
        ))}
        {!live.length ? <span style={{ flexGrow: 1, background: 'var(--c-surface-alt)' }} /> : null}
      </div>
      <div className={s.splitKey}>
        {parts.map((p) => (
          <span key={p.id} data-zero={p.value > 0 ? undefined : ''}><span className={s.sw} style={{ background: p.color }} />{p.label}<b className="num">{format(p.value)}</b></span>
        ))}
      </div>
      <Tip tip={tip} />
    </div>
  );
}

/** Money in / out of the cash forecast (validated on the card surface: blue ↔ amber, ΔE 28 under CVD). */
export const FLOW = { in: '#3F7FE6', out: '#C98500' };

/**
 * The 12-week cash forecast (CFO 5.3 §2): money in as a bar up, money out as a bar down, the running balance as a 2px
 * line on the same dollar axis. The first week the balance goes below zero is banded and labelled (icon + words, not
 * colour alone). Hover a week for its numbers and its biggest items.
 *   weeks: [{ week, from, to, in, out, balance, items: [{ dir, label, amount }] }]; dipWeek = the week number or null.
 */
export function CashForecast({ weeks = [], dipWeek = null, format = (v) => String(v), height = 260 }) {
  const { box, tip, show, hide } = useTip();
  const W = useWidth(box);
  if (!weeks.length) return null;
  const hi = Math.max(1, ...weeks.map((w) => Math.max(w.in || 0, w.balance || 0)));
  const lo = Math.min(0, ...weeks.map((w) => Math.min(-(w.out || 0), w.balance || 0)));
  const step = niceStep((hi - lo) / 4);
  const top = Math.ceil(hi / step) * step; const bottom = Math.floor(lo / step) * step;
  const padL = 56; const padB = 26; const padT = 22;
  const H = height;
  const cw = (W - padL) / weeks.length;
  const bw = Math.min(26, cw * 0.36);
  const y = (v) => padT + (H - padT - padB) * ((top - v) / Math.max(1, top - bottom));
  const grid = []; for (let g = bottom; g <= top + 1e-6; g += step) grid.push(Math.round(g));
  const cx = (i) => padL + i * cw + cw / 2;
  const line = weeks.map((w, i) => `${i ? 'L' : 'M'}${cx(i).toFixed(1)},${y(w.balance || 0).toFixed(1)}`).join(' ');
  const dipI = weeks.findIndex((w) => w.week === dipWeek);
  const day = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <div className={s.chart} ref={box} onMouseLeave={hide}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={s.svg} role="img" aria-label="12-week cash forecast">
        {dipI >= 0 ? <rect x={padL + dipI * cw + 1} y={padT - 18} width={cw - 2} height={H - padT - padB + 18} fill="var(--c-danger)" opacity=".1" rx="4" /> : null}
        {grid.map((g) => (
          <g key={g}>
            <line x1={padL} x2={W} y1={y(g)} y2={y(g)} className={s.grid} style={g === 0 ? { stroke: 'var(--c-border-strong)' } : null} />
            <text x={padL - 8} y={y(g) + 4} className={s.axis} textAnchor="end">{format(g)}</text>
          </g>
        ))}
        {weeks.map((w, i) => {
          const x0 = cx(i);
          const inH = Math.max(0, y(0) - y(w.in || 0) - 1); const outH = Math.max(0, y(-(w.out || 0)) - y(0) - 1);
          const items = [...(w.items || [])].sort((a, b) => b.amount - a.amount).slice(0, 5);
          return (
            <g key={w.week} onMouseMove={(e) => show(e, (
              <>
                <div className={s.tipHead}>Week {w.week} · {day(w.from)}<span>{format(w.balance)}</span></div>
                <div className={s.tipRow}><span className={s.sw} style={{ background: FLOW.in }} />In<span>{format(w.in || 0)}</span></div>
                <div className={s.tipRow}><span className={s.sw} style={{ background: FLOW.out }} />Out<span>{format(w.out || 0)}</span></div>
                {items.map((it, k) => <div key={k} className={s.tipRow} style={{ fontSize: 11 }}>{it.dir === 'in' ? '+' : '−'} {it.label}<span>{format(it.amount)}</span></div>)}
              </>
            ))}>
              <rect x={padL + i * cw} y={padT} width={cw} height={H - padT - padB} fill="transparent" />
              {inH > 0 ? <rect x={x0 - bw - 1} y={y(w.in)} width={bw} height={inH} fill={FLOW.in} rx="3" /> : null}
              {outH > 0 ? <rect x={x0 + 1} y={y(0) + 1} width={bw} height={outH} fill={FLOW.out} rx="3" /> : null}
              <text x={x0} y={H - 8} className={s.axis} textAnchor="middle">{i % (cw < 46 ? 2 : 1) === 0 ? day(w.from) : ''}</text>
            </g>
          );
        })}
        <path d={line} fill="none" stroke="var(--c-text)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" pointerEvents="none" />
        {weeks.map((w, i) => <circle key={w.week} cx={cx(i)} cy={y(w.balance || 0)} r={i === dipI ? 5 : 3} fill={i === dipI ? 'var(--c-danger)' : 'var(--c-text)'} stroke="var(--c-surface)" strokeWidth="2" pointerEvents="none" />)}
        {dipI >= 0 ? <text x={cx(dipI)} y={padT - 6} className={s.val} textAnchor="middle">▼ below zero</text> : null}
        <text x={cx(weeks.length - 1)} y={y(weeks[weeks.length - 1].balance || 0) - 9} className={s.val} textAnchor="end">{format(weeks[weeks.length - 1].balance || 0)}</text>
      </svg>
      <div className={s.legend}>
        <span><span className={s.sw} style={{ background: FLOW.in }} />Money in</span>
        <span><span className={s.sw} style={{ background: FLOW.out }} />Money out</span>
        <span><span className={s.sw} style={{ background: 'var(--c-text)', height: 2, borderRadius: 0 }} />Balance</span>
      </div>
      <Tip tip={tip} />
    </div>
  );
}

/**
 * Lines over time (Sales' cards, Oct 16): `series` = [{ id, label, color, dash? ('4 3' | '2 3'), points: [{ key, label, value | null }] }],
 * every series over the same x positions (index-aligned; a null leaves a gap). One y-axis on round numbers, a dashed
 * gold `target`, the last point dotted, a hover tooltip with every series at that x; one series also gets the
 * Sparkline's soft fill. Series are told apart by dash as well as colour. `format(v)` → text; `xEvery` thins the
 * x labels (auto by width).
 */
export function LineChart({ series = [], height = 200, format = (v) => String(v), target = null, targetLabel = null, xEvery = null, min0 = true }) {
  const { box, tip, show, hide } = useTip();
  const W = useWidth(box);
  const live = series.filter((sr) => sr?.points?.length);
  const n = Math.max(0, ...live.map((sr) => sr.points.length));
  if (!n) return <div style={{ height }} />;
  const vals = live.flatMap((sr) => sr.points.map((p) => p.value)).filter((v) => v != null && Number.isFinite(v));
  const hi = Math.max(target || 0, ...vals) > 0 ? Math.max(target || 0, ...vals) : 1;   // nothing yet → a 0–1 axis, never 1e-9
  const lo = min0 ? 0 : Math.min(target ?? Infinity, ...vals);
  const step = niceStep((hi - lo) / 2 || 1);
  const max = Math.ceil(hi / step) * step || step;
  const base = min0 ? 0 : Math.floor(lo / step) * step;
  const H = height; const padL = 46; const padB = 24; const padT = 12; const padR = 10;
  const x = (i) => (n === 1 ? (W + padL) / 2 : padL + (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - (v - base) / Math.max(1e-9, max - base));
  const grid = Array.from({ length: Math.round((max - base) / step) + 1 }, (_, k) => base + k * step);
  const every = xEvery || Math.max(1, Math.ceil(n / Math.max(1, Math.floor((W - padL) / 56))));
  const labels = live[0].points;
  const path = (pts) => {
    let d = ''; let pen = false;
    pts.forEach((p, i) => { if (p.value == null) { pen = false; return; } d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)} `; pen = true; });
    return d.trim();
  };
  const gid = `line${Math.round(Math.random() * 1e9)}`;
  const lastIdx = (pts) => { for (let i = pts.length - 1; i >= 0; i--) if (pts[i].value != null) return i; return -1; };
  return (
    <div className={s.chart} ref={box} onMouseLeave={hide}
      onMouseMove={(e) => {
        const r = box.current.getBoundingClientRect();
        const i = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left - padL) / Math.max(1, W - padL - padR)) * (n - 1))));
        show(e, (
          <>
            <div className={s.tipHead}>{labels[i]?.tip || labels[i]?.label}{live.length === 1 ? <span>{live[0].points[i]?.value == null ? '—' : format(live[0].points[i].value)}</span> : null}</div>
            {live.length > 1 ? live.map((sr) => <div key={sr.id} className={s.tipRow}><span className={s.sw} style={{ background: sr.color }} />{sr.label}<span>{sr.points[i]?.value == null ? '—' : format(sr.points[i].value)}</span></div>) : null}
            {target != null ? <div className={s.tipRow}><span className={s.sw} style={{ background: 'var(--c-gold)' }} />{targetLabel || 'target'}<span>{format(target)}</span></div> : null}
          </>
        ));
      }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={s.svg} role="img">
        <defs>{live.length === 1 ? <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={live[0].color} stopOpacity=".2" /><stop offset="1" stopColor={live[0].color} stopOpacity="0" /></linearGradient> : null}</defs>
        {grid.map((g) => (
          <g key={g}>
            <line x1={padL} x2={W - padR} y1={y(g)} y2={y(g)} className={s.grid} />
            <text x={padL - 8} y={y(g) + 4} className={s.axis} textAnchor="end">{format(g)}</text>
          </g>
        ))}
        {target != null ? <line x1={padL} x2={W - padR} y1={y(target)} y2={y(target)} className={s.target} /> : null}
        {live.length === 1 && lastIdx(live[0].points) >= 0 ? (() => {
          const pts = live[0].points; const segs = []; let cur = [];
          pts.forEach((p, i) => { if (p.value == null) { if (cur.length) segs.push(cur); cur = []; } else cur.push(i); });
          if (cur.length) segs.push(cur);
          return segs.filter((sg) => sg.length > 1).map((sg) => <path key={sg[0]} fill={`url(#${gid})`} d={`M${x(sg[0]).toFixed(1)},${(H - padB).toFixed(1)} ${sg.map((i) => `L${x(i).toFixed(1)},${y(pts[i].value).toFixed(1)}`).join(' ')} L${x(sg[sg.length - 1]).toFixed(1)},${(H - padB).toFixed(1)} Z`} />);
        })() : null}
        {live.map((sr) => {
          const li = lastIdx(sr.points);
          return (
            <g key={sr.id}>
              <path d={path(sr.points)} fill="none" stroke={sr.color} strokeWidth="2" strokeDasharray={sr.dash || undefined} strokeLinejoin="round" strokeLinecap="round" />
              {sr.points.map((p, i) => (p.value != null && (sr.points.filter((q) => q.value != null).length === 1 || (i > 0 && sr.points[i - 1].value == null && (i === n - 1 || sr.points[i + 1]?.value == null))) ? <circle key={p.key} cx={x(i)} cy={y(p.value)} r="3" fill={sr.color} /> : null))}
              {li >= 0 ? <circle cx={x(li)} cy={y(sr.points[li].value)} r="4.5" fill={sr.color} stroke="var(--c-surface)" strokeWidth="2" /> : null}
            </g>
          );
        })}
        {labels.map((p, i) => ((i === n - 1 || (i % every === 0 && x(n - 1) - x(i) > 64)) ? <text key={p.key} x={x(i)} y={H - 6} className={s.axis} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>{p.label}</text> : null))}
      </svg>
      <Tip tip={tip} />
    </div>
  );
}
